import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const DEFAULT_LANDING_SETTINGS = {
  hero: {
    title: 'No es una plantilla:',
    highlight: 'es el CRM hecho para tu negocio',
    description: 'Reservas, ventas, pagos, equipo y clientes en un solo sistema construido a partir de cómo tu negocio realmente trabaja — no de una plantilla genérica.',
    ctaText: '¿Qué necesitas para tu negocio?',
    ctaLink: '#lead-form',
  },
  features: [
    {
      id: 'records',
      title: 'Expediente único de cliente',
      desc: 'Toda la información de cada cliente en un solo lugar — sin duplicados, sin datos perdidos entre formularios.',
      mediaType: 'video',
      media: '/bookme/video1.mp4',
    },
    {
      id: 'pos',
      title: 'POS Inteligente',
      desc: 'Maneja las citas creadas y los balances por día y por cita.',
      mediaType: 'image',
      media: '/bookme/IMG_0134.jpg',
    },
    {
      id: 'store',
      title: 'Tienda Online',
      desc: 'Vende productos y servicios desde la web.',
      mediaType: 'video',
      media: '/bookme/video2.mp4',
    },
    {
      id: 'sms',
      title: 'SMS y Confirmaciones',
      desc: 'Mantén a tus clientes y tu equipo actualizados con las notificaciones.',
      mediaType: 'image',
      media: '/bookme/tex1.jpeg',
    },
    {
      id: 'workflow',
      title: 'Flujo de trabajo a tu medida',
      desc: 'El sistema sigue tus etapas reales — desde el primer contacto hasta el cobro final — no las de una plantilla.',
      mediaType: 'image',
      media: '/bookme/IMG_0135.jpg',
    },
    {
      id: 'roles',
      title: 'Roles y permisos por equipo',
      desc: 'Cada persona de tu equipo ve solo lo que necesita: ventas, operaciones, campo o administración.',
      mediaType: 'image',
      media: '/bookme/IMG_0136.jpg',
    },
  ],
  industries: [
    {
      id: 'spa',
      title: 'Spas y clínicas estéticas',
      desc: 'Agenda multi-especialista, portal del paciente con seguimiento clínico, tienda y pagos integrados.',
    },
    {
      id: 'restauracion',
      title: 'Restauración y seguros',
      desc: 'Flujo de lead a trabajo cerrado: evaluación, estimado, aprobación, contrato firmado y cobro, con SLAs por etapa.',
    },
    {
      id: 'servicios',
      title: 'Negocios de servicios en general',
      desc: 'Si tu negocio agenda, cobra y da seguimiento a clientes, el sistema se configura a tu proceso, no al revés.',
    },
  ],
  caseStudies: [
    {
      id: 'medicalast',
      name: 'Medical Aesthetics',
      sector: 'Clínica de estética médica, Puerto Rico',
      desc: 'Agenda con selección de especialista y duración real por rol, portal del paciente con seguimiento post-tratamiento y firma digital de consentimientos, tienda con checkout y pagos integrados, y certificados de regalo digitales.',
      // TODO: reemplazar con capturas reales del sistema (sin datos de pacientes reales visibles) una vez autorizadas.
      media: ['/bookme/IMG_0137.jpg', '/bookme/IMG_0138.jpg'],
    },
  ],
  testimonials: [
    {
      name: 'Carla, Spa Natural',
      text: 'Desde que usamos Book Me, nuestras reservas aumentaron un 40%. ¡Es súper fácil de usar!',
    },
    {
      name: 'Luis, Barbería Pro',
      text: 'Automatizamos todo: citas, pagos y recordatorios. Ya no tengo que usar WhatsApp manual.',
    },
    {
      name: 'María, Estética Glow',
      text: 'Lo mejor fue la atención personalizada. El sistema se adaptó a lo que yo necesitaba.',
    },
  ],
  gallery: ['/bookme/IMG_0134.jpg', '/bookme/IMG_0135.jpg', '/bookme/IMG_0136.jpg', '/bookme/IMG_0137.jpg', '/bookme/IMG_0138.jpg'],
  whatsapp: {
    number: '15558364840',
    message: 'Hola, tengo preguntas sobre el sistema Book Me',
    labelMobile: 'Hablar con representante',
    labelDesktop: '¿Tienes preguntas? Habla con un representante ahora',
  },
};

export const useLandingSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_LANDING_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'landing');
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          if (isMounted) {
            setSettings(DEFAULT_LANDING_SETTINGS);
            setLoading(false);
          }
          return;
        }
        const data = snap.data() || {};
        if (isMounted) {
          setSettings({
            ...DEFAULT_LANDING_SETTINGS,
            ...data,
            hero: {
              ...DEFAULT_LANDING_SETTINGS.hero,
              ...(data.hero || {}),
            },
            whatsapp: {
              ...DEFAULT_LANDING_SETTINGS.whatsapp,
              ...(data.whatsapp || {}),
            },
            features: Array.isArray(data.features) && data.features.length ? data.features : DEFAULT_LANDING_SETTINGS.features,
            testimonials:
              Array.isArray(data.testimonials) && data.testimonials.length
                ? data.testimonials
                : DEFAULT_LANDING_SETTINGS.testimonials,
            gallery:
              Array.isArray(data.gallery) && data.gallery.length ? data.gallery : DEFAULT_LANDING_SETTINGS.gallery,
          });
          setLoading(false);
        }
      } catch (err) {
        console.error('❌ Error cargando landing settings:', err);
        if (isMounted) {
          setError(err);
          setSettings(DEFAULT_LANDING_SETTINGS);
          setLoading(false);
        }
      }
    };

    fetchSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  return { settings, loading, error };
};

export default useLandingSettings;
